import { useMemo, useRef, useCallback } from 'react'
import { useHistory } from 'react-router-dom'
import { Text, Flex, useModal, UserDisabledIcon, TimerIcon } from 'vvs-uikit'
import { GetGotchiOwnerDocument, useUpdateTeamMembersMutation } from 'vvs-gotchi-graphql'
import { useTranslation } from 'contexts/Localization'
import { toastTypes } from 'components/Toast'
import useToast from 'hooks/useToast'
import useActiveWeb3React from 'hooks/useActiveWeb3React'
import { Mole } from 'state/vvsgotchi/models'
import { useGotchiAuth } from 'state/vvsgotchi/hooks/useAuth'
import useGotchiOwner from 'state/vvsgotchi/hooks/useGotchiOwner'
import { RunningModal } from 'views/VvsGotchi/components/Modal'
import ActionButton from 'views/VvsGotchi/components/ActionButton'
import getTeamSelectUnlockAt from 'views/VvsGotchi/utils/getTeamSelectUnlockAt'
import { useTheme } from 'styled-components'
import { useHourCountdown } from 'hooks/useCountdown'
import { Path } from 'config/constants/paths'
import historyBackOrReplace from 'utils/historyBackOrReplace'

const RemoveFromTeamBtn: React.FC<{
  mole: Mole
}> = ({ mole }) => {
  const { t } = useTranslation()
  const history = useHistory()
  const { account } = useActiveWeb3React()
  const [, ensureAuth] = useGotchiAuth()
  const { toastVVSGotchi } = useToast()
  const { owner, team, refresh } = useGotchiOwner()
  const [updateTeamMembers] = useUpdateTeamMembersMutation()

  const teamRef = useRef(team)
  teamRef.current = team

  const teamSelectUnlockAt = useMemo(() => getTeamSelectUnlockAt(owner?.lockedMoles, team), [owner?.lockedMoles, team])
  const isLocked = mole.isLocked || teamSelectUnlockAt !== undefined
  const unlockAt = isLocked ? Math.max(new Date(mole.unlockAt).getTime() || 0, teamSelectUnlockAt || 0) : 0

  const runningModalSwitches = useModal(
    <RunningModal>{t('Removing...')}</RunningModal>,
    false,
    false,
    'vvsgotchiFeeding',
  )
  const runningModalSwitchesRef = useRef(runningModalSwitches)
  runningModalSwitchesRef.current = runningModalSwitches

  const onRemove = useCallback(async () => {
    const [openUpdatingModal, closeUpdatingModal] = runningModalSwitchesRef.current

    await ensureAuth()
    openUpdatingModal()
    try {
      await updateTeamMembers({
        variables: {
          owner: account,
          tokenIds: teamRef.current.map((m) => m.tokenId).filter((id) => id !== mole.tokenId),
        },
        refetchQueries: [GetGotchiOwnerDocument],
      })
      await refresh()
      toastVVSGotchi(
        t('Successfully updated your team ( ͡° ͜ʖ ͡° )'),
        toastTypes.INFO,
        <UserDisabledIcon fill="black" width="16px" />,
      )
      historyBackOrReplace(history, Path.VVS_GOTCHI_TEAM)
    } catch (err) {
      console.error(err)
    } finally {
      closeUpdatingModal()
    }
  }, [account, updateTeamMembers, mole, history, toastVVSGotchi, refresh, ensureAuth, t])

  return (
    <>
      {isLocked && <LockCountdown time={unlockAt} />}
      <ActionButton disabled={isLocked} onClick={onRemove}>
        {t('Remove from Team')}
      </ActionButton>
    </>
  )
}

export default RemoveFromTeamBtn

const LockCountdown = ({ time }: { time: number }) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const { refresh } = useGotchiOwner()
  const timeExcerptMap = useHourCountdown(time, refresh)

  return (
    <Flex mt={-2} mb={2}>
      <TimerIcon width="12px" height="14px" fill={theme.colors.lightBeige} ml={1} mr={2} />
      <Text color={theme.colors.lightBeige} fontSize="13px" fontWeight="700">
        {t('Unlock in %untilResetTime%', {
          untilResetTime: timeExcerptMap.h > 0 ? t('%h%hr %m%m %s%s', timeExcerptMap) : t('%m%m %s%s', timeExcerptMap),
        })}
      </Text>
    </Flex>
  )
}
